import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BranchCode } from "api/risk";
import { UpdateCopropertyComplementsCommand } from "api/coproperty";
import { BranchStepConfigFactory, createStepConfig } from "features/subscription-process/navigation/Steps/createStepConfig";
import { StepPageWrapper } from "features/subscription-process/navigation/Steps/StepPageWrapper";
import { useStepNavigation } from "features/subscription-process/navigation/Steps/useStepNavigation";
import { GenericLoading } from "components/ui/Loader";
import { MriStepIdentifier } from "../types/enums";
import { MRI_ROUTES } from "../utils/mriRoutes";
import { CopropertyComplementsForm } from "./CopropertyComplementsForm";
import { CopropertyIdentityCard } from "./CopropertyIdentityCard";
import { CopropertyRecapPanel } from "./CopropertyRecapPanel";
import { CopropertyStepLayout } from "./CopropertyStepLayout";
import { useCoproperty } from "./useCoproperty";

type BranchStepConfig = Partial<Record<BranchCode, BranchStepConfigFactory>>;

/**
 * Sous-étape « compléments » du dossier MRI (écrans 14-15), après les bâtiments : contrat
 * en cours et prochaine AG. Passable : le bouton d'étape enregistre et continue vers la
 * sinistralité, le lien « Passer cette étape » y va sans enregistrer.
 */
export const CopropertyComplementsPage = () => {
  const { riskId, copropertyId, coproperty, isLoading, updateComplements } = useCoproperty();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasCoproperty = copropertyId != null;

  useEffect(() => {
    if (!riskId || hasCoproperty) return;

    toast.warn("Rattachez d'abord une copropriété.");
    navigate(`${MRI_ROUTES.copropertyRegistration}/${riskId}`, { replace: true });
  }, [riskId, hasCoproperty, navigate]);

  const buildCommandRef = useRef<(() => UpdateCopropertyComplementsCommand | null) | null>(null);
  const registerBuildCommand = useCallback((build: () => UpdateCopropertyComplementsCommand | null) => {
    buildCommandRef.current = build;
  }, []);

  const claimsRoute = `${MRI_ROUTES.copropertyClaims}/${riskId}`;

  const saveAndContinue = useCallback(async () => {
    const command = buildCommandRef.current?.();
    if (!command) return;

    setIsSubmitting(true);
    try {
      if (await updateComplements(command)) {
        navigate(claimsRoute);
      }
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue.");
    } finally {
      setIsSubmitting(false);
    }
  }, [updateComplements, navigate, claimsRoute]);

  const branchStepConfig = useMemo<BranchStepConfig>(
    () => ({
      [BranchCode.Mri]: createStepConfig({
        prev: {
          identifier: MriStepIdentifier.CopropertyBuildings,
          route: MRI_ROUTES.copropertyBuildings,
          label: "Retour aux bâtiments",
        },
        next: {
          identifier: MriStepIdentifier.CopropertyClaims,
          route: MRI_ROUTES.copropertyClaims,
          label: isSubmitting ? "Enregistrement…" : "Enregistrer et continuer",
          disabled: isSubmitting,
          onClick: saveAndContinue,
        },
      }),
    }),
    [isSubmitting, saveAndContinue]
  );
  const { stepConfig, goToNext, goToPrevious } = useStepNavigation(branchStepConfig);

  if (!hasCoproperty) {
    return <GenericLoading isPageCentered />;
  }

  return (
    <div className="mx-auto mt-8 max-w-screen-2xl p-4 md:p-8">
      <StepPageWrapper stepConfig={stepConfig} onNext={goToNext} onPrevious={goToPrevious} containerSize="full">
        {isLoading || !coproperty ? (
          <GenericLoading isPageCentered />
        ) : (
          <CopropertyStepLayout
            title="Compléments du dossier"
            message="Étape facultative : renseignez ce que vous connaissez du contrat en cours, ou passez directement à la sinistralité."
            aside={
              <>
                <CopropertyIdentityCard coproperty={coproperty} />
                <CopropertyRecapPanel
                  coproperty={coproperty}
                  hideIdentity
                  showRetainedArea
                  checklist={[
                    { label: "Copropriété rattachée", status: "done" },
                    { label: "Questionnaire éligible", status: "done" },
                    { label: "Bâtiments décrits", status: "done" },
                    { label: "Compléments en cours", status: "active" },
                    { label: "Sinistralité à déclarer", status: "todo" },
                  ]}
                />
              </>
            }
          >
            <CopropertyComplementsForm coproperty={coproperty} registerBuildCommand={registerBuildCommand} />
            <div className="flex justify-end">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => navigate(claimsRoute)}
                className="text-[13px] font-medium text-gray-600 underline hover:text-gray-900 disabled:opacity-50"
              >
                Passer cette étape
              </button>
            </div>
          </CopropertyStepLayout>
        )}
      </StepPageWrapper>
    </div>
  );
};
